import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { X, CheckCircle2, RefreshCw, PauseCircle, XCircle } from "lucide-react";

const DECISIONS = [
  ["PROCEED", "Proceed", "bg-emerald-600 text-white", CheckCircle2],
  ["SECOND_INTERVIEW", "Second Interview", "bg-sky-600 text-white", RefreshCw],
  ["HOLD", "Hold", "bg-amber-500 text-white", PauseCircle],
  ["NOT_A_FIT", "Not a Fit", "bg-rose-600 text-white", XCircle],
];

const SCORE_FIELDS = [
  ["role_fit", "Role fit"],
  ["communication", "Communication"],
  ["experience_match", "Experience match"],
  ["skills_demonstrated", "Skills demonstrated"],
];

export default function InterviewScorecard({ interview, onClose, onSubmitted }) {
  const [decision, setDecision] = useState("");
  const [scores, setScores] = useState({role_fit:0, communication:0, experience_match:0, skills_demonstrated:0});
  const [strengths, setStrengths] = useState("");
  const [concerns, setConcerns] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!decision) { toast.error("Pick a decision"); return; }
    setBusy(true);
    try {
      await api.post(`/interviews/${interview.id}/scorecard`, {
        interview_id: interview.id, proceed_decision: decision,
        ...scores, strengths: strengths || null, concerns: concerns || null,
        feedback_notes: notes || null,
      });
      toast.success("Scorecard submitted");
      onSubmitted?.(); onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/80 z-50 overflow-auto p-4 flex items-start justify-center" onClick={onClose} data-testid="scorecard-modal">
      <div className="max-w-2xl w-full bg-white rounded-xl p-6 mt-8" onClick={e=>e.stopPropagation()}>
        <div className="flex justify-between items-start mb-5">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600 mb-1">Interview Scorecard</div>
            <h2 className="font-display text-2xl font-bold text-slate-900">{interview.interview_type || "Interview"}</h2>
            <div className="text-xs text-slate-500 mt-1">Candidate {interview.candidate_id?.slice(0,8)} · Job {interview.job_order_id?.slice(0,8)}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18}/></button>
        </div>

        <div className="mb-5">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 mb-2">Decision</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {DECISIONS.map(([val, lbl, cls, Icon]) => (
              <button key={val} data-testid={`sc-dec-${val}`} onClick={()=>setDecision(val)}
                      className={`h-10 rounded text-xs font-semibold inline-flex items-center justify-center gap-1.5 border-2 transition ${decision===val?cls+" border-transparent":"bg-white border-slate-300 text-slate-700 hover:border-slate-500"}`}>
                <Icon size={13}/> {lbl}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-5 grid md:grid-cols-2 gap-3">
          {SCORE_FIELDS.map(([k, lbl]) => (
            <div key={k}>
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 mb-1">{lbl} <span className="text-slate-400 normal-case tracking-normal">({scores[k]}/5)</span></div>
              <div className="flex gap-1">
                {[1,2,3,4,5].map(n => (
                  <button key={n} data-testid={`sc-${k}-${n}`} onClick={()=>setScores(s=>({...s,[k]:n}))}
                          className={`w-9 h-9 rounded text-xs font-semibold border ${scores[k]>=n?"bg-slate-900 text-white border-slate-900":"bg-white text-slate-500 border-slate-300"}`}>{n}</button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-3 mb-5">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 mb-1">Strengths</div>
            <textarea data-testid="sc-strengths" rows={2} value={strengths} onChange={e=>setStrengths(e.target.value)} className="w-full px-3 py-2 rounded border border-slate-300 text-sm resize-none" placeholder="What stood out"/>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 mb-1">Concerns</div>
            <textarea data-testid="sc-concerns" rows={2} value={concerns} onChange={e=>setConcerns(e.target.value)} className="w-full px-3 py-2 rounded border border-slate-300 text-sm resize-none" placeholder="What gave you pause"/>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 mb-1">Feedback notes</div>
            <textarea data-testid="sc-notes" rows={2} value={notes} onChange={e=>setNotes(e.target.value)} className="w-full px-3 py-2 rounded border border-slate-300 text-sm resize-none" placeholder="Optional"/>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 rounded border border-slate-300 text-slate-700 text-sm font-semibold">Cancel</button>
          <button data-testid="sc-submit" onClick={submit} disabled={busy || !decision} className="h-10 px-4 rounded bg-slate-900 text-white text-sm font-semibold disabled:opacity-50">Submit scorecard</button>
        </div>
      </div>
    </div>
  );
}
